// Simple auth utilities for token handling (JWT access/refresh)

export const TOKEN_KEY = 'access_token';
export const TOKEN_EXPIRES_AT_KEY = 'access_token_expires_at';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const AUTH_BYPASS_KEY = 'auth_bypass';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.alumni.uem.mz';
const REFRESH_URL = (process.env.REACT_APP_REFRESH_URL || '/api/token/refresh/');

export function isAuthenticated() {
  const token = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  const expiresAtStr = sessionStorage.getItem(TOKEN_EXPIRES_AT_KEY) || localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!token) return false;
  if (expiresAtStr) {
    const expiresAt = parseInt(expiresAtStr, 10);
    if (Number.isFinite(expiresAt)) {
      return Date.now() < expiresAt - 5000; // 5s skew
    }
  }
  return true;
}

// Save tokens from API that returns { access, refresh }
export function saveTokens({ access, refresh }, persist = false) {
  const storage = persist ? localStorage : sessionStorage;
  if (access) storage.setItem(TOKEN_KEY, access);
  if (refresh) storage.setItem(REFRESH_TOKEN_KEY, refresh);

  // Try to extract exp from JWT access token
  try {
    if (access) {
      const [, payload] = access.split('.');
      if (payload) {
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        const exp = json && json.exp;
        if (exp && Number.isFinite(exp)) {
          storage.setItem(TOKEN_EXPIRES_AT_KEY, String(exp * 1000));
        }
      }
    }
  } catch (_) {
    // ignore parse errors
  }
}

export function clearAuth() {
  [sessionStorage, localStorage].forEach((s) => {
    s.removeItem(TOKEN_KEY);
    s.removeItem(REFRESH_TOKEN_KEY);
    s.removeItem(TOKEN_EXPIRES_AT_KEY);
    s.removeItem('user');
    s.removeItem(AUTH_BYPASS_KEY);
  });
}

export function getAccessToken() {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}

export function getAuthHeader() {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Request new access token using refresh token
export async function refreshAccessToken() {
  const refresh = sessionStorage.getItem(REFRESH_TOKEN_KEY) || localStorage.getItem(REFRESH_TOKEN_KEY);
  const persist = !!localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refresh) throw new Error('Sem refresh token');

  const resp = await fetch(API_BASE + REFRESH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  });

  if (!resp.ok) {
    clearAuth();
    throw new Error(`Falha no refresh (${resp.status})`);
  }

  const data = await resp.json(); // { access, refresh? }
  saveTokens(data, persist);
  return data.access;
}

// fetch wrapper that retries once after refreshing on 401
export async function authFetch(input, init = {}) {
  const headers = { ...(init.headers || {}), ...getAuthHeader() };
  let resp = await fetch(input, { ...init, headers });
  if (resp.status !== 401) return resp;

  try {
    await refreshAccessToken();
    const headers2 = { ...(init.headers || {}), ...getAuthHeader() };
    resp = await fetch(input, { ...init, headers: headers2 });
  } catch (_) {
    clearAuth();
  }
  return resp;
}
